import { InvoiceLineItem } from 'shared/types/graphql';
import { FilteredLineItemsType } from '../components/Modals/InvoicePreview/Invoice';

// TO DO: Create unit tests for the filterLineItems function to ensure that rounding of fee portions always adds up to the total service fee accurately.
// Test cases should cover scenarios with even and odd numbers of line items, as well as cases with heavily decimalized values.
// There are some real-world examples that inspired this function that can be used as test cases.

/**
 * Distribute a service fee proportionally across multiple line items, ensuring each item is rounded in a way that prevents cumulative rounding errors.
 * @param feePortions - Array of fee portions to be distributed across line items
 * @returns Array of rounded fee portions
 */

export const adjustFeePortions = (feePortions: number[]): number[] => {
  if (feePortions.length <= 1) return feePortions;

  const newFeePortions: number[] = new Array(feePortions.length).fill(0);
  let prevDecimal = 0;

  for (let i = 0; i < feePortions.length; i++) {
    // Extract the integer part and add any accumulated decimal
    const currentPortion = feePortions[i] + prevDecimal;
    const integerPortion = Math.floor(currentPortion);

    // Store the integer portion in the new fee portions array
    newFeePortions[i] = integerPortion;

    // Calculate and carry over the remaining decimal portion
    prevDecimal = currentPortion - integerPortion;
  }

  // After the loop, if there's any remaining decimal, add it to the last entry to ensure total sum is accurate
  if (prevDecimal > 0) {
    newFeePortions[newFeePortions.length - 1] += prevDecimal;
  }

  return newFeePortions;
};

/**
 * Filter line items to add service fee proportionally to each line item
 * @param invoiceLineItemTotal: number - sum of all line items of type code
 * @param lineItems - Array of line items of type code to be filtered
 * @param serviceFeeToBeAddedToLineItems - Total service fee to be distributed across line items
 */
export interface IFilterLineItemInput {
  invoiceLineItemTotal: number;
  lineItems: InvoiceLineItem[];
  serviceFeeToBeAddedToLineItems: number;
}

export const filterLineItems = ({
  invoiceLineItemTotal,
  lineItems,
  serviceFeeToBeAddedToLineItems,
}: IFilterLineItemInput): FilteredLineItemsType => {
  const feePortions = lineItems.map((item) => {
    return ((item.amount + item.fee) / invoiceLineItemTotal) * serviceFeeToBeAddedToLineItems;
  });

  const roundedFeePortions = adjustFeePortions(feePortions);

  return lineItems
    .map((item, index) => ({
      ...item,
      amount: item.amount + roundedFeePortions[index],
    }))
    .filter((item) => item.amount >= 0);
};
